import * as React from "react"
import { useEvent } from "react-use"
import { ObjectKind } from "@digits-graphql/frontend/graphql-bearer"
import { svgIconStyles } from "@digits-shared/components/SVG/svgIconStyles"
import { SvgAccounting01 } from "@digits-shared/components/SVGIcons/line/Accounting01.svg"
import { SvgBookOpen01 } from "@digits-shared/components/SVGIcons/line/BookOpen01.svg"
import { SvgDataflow04 } from "@digits-shared/components/SVGIcons/line/Dataflow04.svg"
import { SvgMarkerPin02 } from "@digits-shared/components/SVGIcons/line/MarkerPin02.svg"
import { SvgMessageTextSquare02 } from "@digits-shared/components/SVGIcons/line/MessageTextSquare02.svg"
import { SvgScales02 } from "@digits-shared/components/SVGIcons/line/Scales02.svg"
import { SvgShieldTick } from "@digits-shared/components/SVGIcons/line/ShieldTick.svg"
import { SvgTags } from "@digits-shared/components/SVGIcons/line/Tags.svg"
import { SvgChevronDownSolid } from "@digits-shared/components/SVGIcons/solid/ChevronDownSolid.svg"
import zIndexes from "@digits-shared/DesignSystem/zIndexes"
import stringHelper from "@digits-shared/helpers/stringHelper"
import { useCurrentRoute } from "@digits-shared/hooks/useCurrentRoute"
import { useMeasure } from "@digits-shared/hooks/useMeasure"
import { usePopOverState } from "@digits-shared/hooks/usePopOverState"
import useSession from "@digits-shared/hooks/useSession"
import { AspectCode } from "@digits-shared/session/AspectCode"
import colors from "@digits-shared/themes/colors"
import styled from "styled-components"
import { BadgeName, useBadgeContext } from "src/frontend/components/Shared/Contexts/BadgeContext"
import {
  ACTIVE_CLASSNAME,
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_PATH_SVG_STYLES,
} from "src/frontend/components/Shared/NavSidebar/sidebarConstants"
import { SidebarRow } from "src/frontend/components/Shared/NavSidebar/SidebarExpando"
import { RequiresDigitsEmployee } from "src/frontend/components/Shared/Permissions/Requires"
import { RequiresAspect } from "src/frontend/components/Shared/Permissions/RequiresAspect"
import routes from "src/frontend/routes"
import type FrontendSession from "src/frontend/session"

const ACCOUNTING_ROUTES = [
  routes.accounting,
  routes.reconciliations,
  routes.transactionReview,
  routes.qualityCheck,
]

/*
  STYLES
*/

const AccountingIcon = styled(SvgAccounting01)`
  ${SIDEBAR_PATH_SVG_STYLES};
`

const LedgerIcon = styled(SvgBookOpen01)`
  ${SIDEBAR_PATH_SVG_STYLES};
`

const ReconciliationsIcon = styled(SvgScales02)`
  ${SIDEBAR_PATH_SVG_STYLES};
`

const TransactionReviewIcon = styled(SvgMessageTextSquare02)`
  ${SIDEBAR_PATH_SVG_STYLES};
`

const QualityCheckIcon = styled(SvgShieldTick)`
  ${SIDEBAR_PATH_SVG_STYLES};
`

const CategoryManagerIcon = styled(SvgTags)`
  ${SIDEBAR_PATH_SVG_STYLES};
`

const DepartmentsIcon = styled(SvgDataflow04)`
  ${SIDEBAR_PATH_SVG_STYLES};
`

const LocationsIcon = styled(SvgMarkerPin02)`
  ${SIDEBAR_PATH_SVG_STYLES};
`

const AccountingTitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
`

const MenuChevron = styled(SvgChevronDownSolid)`
  width: 20px;
  height: 20px;
  margin: -2px 0;
  ${svgIconStyles(colors.secondary)};

  transition: transform 250ms;
  .accounting-menu-open & {
    transform: rotate(-180deg);
  }
`

const MenuHoverContainer = styled.div`
  position: relative;
  border-radius: 4px;
  width: calc(100% - 32px);
  transition: background-color 350ms ease-out;

  @container sidebar (min-width: ${SIDEBAR_COLLAPSED_WIDTH + 1}px) {
    &.accounting-menu-open {
      background-color: ${colors.translucentAccentBlue10};
    }
  }
`

const MenuSidebarRow = styled(SidebarRow)`
  width: 100%;
  margin: 0;

  .accounting-menu-open > &&& {
    cursor: pointer;
    background: none;
    font-weight: unset;
  }

  @container sidebar (min-width: ${SIDEBAR_COLLAPSED_WIDTH + 1}px) {
    &.${ACTIVE_CLASSNAME}, &:hover.${ACTIVE_CLASSNAME} {
      background: ${colors.white};
    }
  }
`

const MenuWrapper = styled.div<{ height: number }>`
  will-change: height, opacity;
  height: 0;
  opacity: 0;
  margin: 0 6px;
  border-radius: 0 0 4px 4px;

  @container sidebar (max-width: ${SIDEBAR_COLLAPSED_WIDTH}px) {
    margin-left: 1px;
  }

  transition:
    opacity 100ms ease-out,
    height 100ms ease-in-out;

  &.animating {
    overflow-y: hidden;
  }

  pointer-events: none;
  .accounting-menu-open & {
    pointer-events: auto;
    opacity: 1;
    height: ${({ height }) => height}px;
  }
`

const MenuContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 8px;
  padding: 4px 0 8px;
  border-radius: inherit;

  z-index: ${zIndexes.leftSidebarMax};

  ${MenuSidebarRow} {
    &:hover:not(.${ACTIVE_CLASSNAME}) {
      background-color: ${colors.translucentWhite50};
    }
  }
`

/*
  COMPONENT
*/

export const AccountingSidebar: React.FC = () => {
  const { currentLegalEntity } = useSession<FrontendSession>()
  const params = { leSlug: currentLegalEntity?.slug }
  const currentRoute = useCurrentRoute(routes)
  const route = useFirstAccountingMenuRoute()

  const {
    isPopOverOpen: isMenuOpen,
    onMouseEnter,
    onMouseLeave,
    hidePopOver,
  } = usePopOverState({ waitToOpen: 350 })

  const isAccountingApplicationActive = React.useMemo(
    () => ACCOUNTING_ROUTES.some((r) => r.isRouteOrChildOfRoute(currentRoute)),
    [currentRoute]
  )

  const showMenu = isMenuOpen || isAccountingApplicationActive

  return (
    <MenuHoverContainer
      className={stringHelper.classNames(showMenu && "accounting-menu-open")}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <MenuSidebarRow Icon={AccountingIcon} route={route} params={params}>
        <AccountingTitleContainer>
          Accounting
          <MenuChevron />
        </AccountingTitleContainer>
      </MenuSidebarRow>

      <AccountingMenu hideMenu={hidePopOver} inert={!showMenu} />
    </MenuHoverContainer>
  )
}

const AccountingMenu: React.FC<{
  hideMenu: () => void
  inert: boolean
}> = ({ hideMenu, inert }) => {
  const [ref, { height }] = useMeasure<HTMLDivElement>()
  const wrapperRef = React.useRef<HTMLDivElement>(null)

  useEvent(
    "transitionstart",
    () => wrapperRef.current?.classList.add("animating"),
    wrapperRef.current,
    { once: true }
  )
  useEvent(
    "transitionend",
    () => wrapperRef.current?.classList.remove("animating"),
    wrapperRef.current,
    { once: true }
  )

  return (
    <MenuWrapper ref={wrapperRef} height={height} inert={inert}>
      <MenuContainer ref={ref} onClick={hideMenu}>
        <AIBookkeeperFeatures />
        <AuditFeatures />
        <CategoryManager />
        <DimensionsManagers />
      </MenuContainer>
    </MenuWrapper>
  )
}

const AIBookkeeperFeatures: React.FC = () => {
  const { currentLegalEntity, hasAccessToAspect } = useSession<FrontendSession>()
  const params = { leSlug: currentLegalEntity?.slug }
  const showReconciliations = !hasAccessToAspect(AspectCode.RestrictLedgerEdits)

  return (
    // NOTE: If the checks here change, update useFirstAccountingMenuRoute
    <RequiresAspect aspect={AspectCode.AIBookkeeper}>
      <MenuSidebarRow Icon={LedgerIcon} route={routes.accounting} params={params}>
        Ledger
      </MenuSidebarRow>

      {showReconciliations && (
        <MenuSidebarRow Icon={ReconciliationsIcon} route={routes.reconciliations} params={params}>
          Reconciliations
        </MenuSidebarRow>
      )}
    </RequiresAspect>
  )
}

const CategoryManager: React.FC = () => {
  const { currentLegalEntity } = useSession<FrontendSession>()
  const params = { leSlug: currentLegalEntity?.slug }

  return (
    <RequiresAspect aspect={AspectCode.AIBookkeeper}>
      <MenuSidebarRow Icon={CategoryManagerIcon} route={routes.categoryManager} params={params}>
        Category Manager
      </MenuSidebarRow>
    </RequiresAspect>
  )
}

const DimensionsManagers: React.FC = () => {
  const { currentLegalEntity } = useSession<FrontendSession>()
  const departaments = { leSlug: currentLegalEntity?.slug, kind: ObjectKind.Department }
  const locations = { leSlug: currentLegalEntity?.slug, kind: ObjectKind.Location }

  return (
    <RequiresDigitsEmployee>
      <RequiresAspect aspect={AspectCode.AIBookkeeper}>
        <MenuSidebarRow
          Icon={DepartmentsIcon}
          route={routes.dimensionManager}
          params={departaments}
        >
          Departments
        </MenuSidebarRow>
        <MenuSidebarRow Icon={LocationsIcon} route={routes.dimensionManager} params={locations}>
          Locations
        </MenuSidebarRow>
      </RequiresAspect>
    </RequiresDigitsEmployee>
  )
}

const AuditFeatures: React.FC = () => {
  const { currentLegalEntity } = useSession<FrontendSession>()
  const params = { leSlug: currentLegalEntity?.slug }
  const { badgeCount } = useBadgeContext()

  const qcCount = badgeCount(currentLegalEntity.id, BadgeName.QualityCheck) || 0
  const trCount = badgeCount(currentLegalEntity.id, BadgeName.TransactionReview) || 0

  return (
    // NOTE: If the checks here change, update useFirstAccountingMenuRoute
    <RequiresAspect aspect={AspectCode.Audit}>
      <RequiresAspect aspect={AspectCode.AdvancedLedger}>
        <MenuSidebarRow
          Icon={TransactionReviewIcon}
          route={routes.transactionReview}
          params={params}
          badge={trCount}
        >
          Transaction Review
        </MenuSidebarRow>

        <MenuSidebarRow
          Icon={QualityCheckIcon}
          route={routes.qualityCheck}
          params={params}
          badge={qcCount}
        >
          Quality Check
        </MenuSidebarRow>
      </RequiresAspect>
    </RequiresAspect>
  )
}

/**
 * Determine the route for the Accounting menu root node when clicked
 * This is intended to match the first clickable item in the accounting menu,
 * so the checks here need to match the checks based on the features above
 */
function useFirstAccountingMenuRoute() {
  const { hasAccessToAspect } = useSession<FrontendSession>()

  return React.useMemo(() => {
    if (hasAccessToAspect(AspectCode.AIBookkeeper)) {
      return routes.accounting
    }

    if (hasAccessToAspect(AspectCode.Audit) && hasAccessToAspect(AspectCode.AdvancedLedger)) {
      return routes.transactionReview
    }

    // returning undefined makes the root node unclickable, but it still opens on hover
    return undefined
  }, [hasAccessToAspect])
}
